import {SessionManagement } from '../models/constants/authentication.constants';
import {IAuthenticationGateway } from './authentication.gateway';
import * as Repositories from '../repositories/repositories';
import { IUserProfile } from '../models/userprofile.model';
import { ScreenLockService } from '../core/services/screenLockService/screenLock.service';
import { IDeferred } from 'angular';


export interface IAuthenticationService{
    getIdToken() : string;
    getAccessToken(): string;
    setAccessToken(accessToken:string): void;
    getUserProfile(): IUserProfile;
    setUserProfile(userProfile:IUserProfile): void;
    handleCallback(): void;
    login(appState?:string):any;
    logout():void;
    checkForToken():void;
    refreshToken():ng.IPromise<any>;
    isAuthenticated():boolean;
    isInRole(roles: string[]);
}

export class AuthenticationService implements IAuthenticationService {

    private logoutRedirectStateName:string = 'shellFrame.quoteWizardFrame.wizard.returningUserInfo';
    private wizardStateName:string = 'shellFrame.quoteWizardFrame.wizard';

    private accessToken:string;
    private idToken:string;
    private $state: angular.ui.IStateService;
    private userProfile: IUserProfile;
    private userRepository: Repositories.UserRepository;
    private pendingCalls: Array<IDeferred<any>>;

    static $inject = ['$injector', '$timeout', '$q', 'authenticationGateway', 'screenLockService'];

    constructor(private $injector: any,
        private $timeout: angular.ITimeoutService,
        private $q: ng.IQService,
        private authenticationGateway: IAuthenticationGateway,
        private screenLockService: ScreenLockService
    ) {
        this.pendingCalls = [];
    }

    public getIdToken() {
        return this.idToken;
    }

    public getAccessToken() {
        return this.accessToken;
    }

    public setAccessToken(accessToken) {
        this.accessToken = accessToken;

        let isAuthenticated = accessToken != null && accessToken != '';

        localStorage.setItem(SessionManagement.IsAuthenticated, isAuthenticated.toString());
    }

    public getUserProfile() {
        if (this.userProfile == null) {
            let profileCache = localStorage.getItem(SessionManagement.UserProfile);
            if (profileCache != null) {
                this.userProfile = JSON.parse(profileCache);
            }
        }

        return this.userProfile;
    }

    public setUserProfile(userProfile) {
        const profileJson = JSON.stringify(userProfile);
        localStorage.setItem(SessionManagement.UserProfile, profileJson);
        this.userProfile = userProfile;
    }

    public login(redirectOnLogin?: string) {

        this.screenLockService.broadcast(1);

        let stateKey = this.getGuid();

        if (this.$state == null) {
            this.$state = this.$injector.get('$state');
        }

        if (angular.isDefined(redirectOnLogin)) {
            localStorage.setItem(stateKey, redirectOnLogin);
        }

        this.authenticationGateway.login(stateKey);
    }

    public checkForToken() {

        //check local storage first then url
        const tokenCache = localStorage.getItem(SessionManagement.Token);

        if (tokenCache != null) {
            let token = JSON.parse(tokenCache);
            if (token.accessToken != null) {
                this.setAccessToken(token.accessToken);
            }
        }
        else {
            this.handleCallback();
        }
    }

    public handleCallback(): void {
        const service = this;
        this.authenticationGateway.parseUrlHashFragment().then(function (jwt) {
            if (jwt && jwt.accessToken && jwt.idToken) {
                service.handleLogin(jwt);
            }
            else {
                service.login();
            }
        }, function () {
            service.$timeout(function () {
                service.login();
            });
        })
    }

    public handleLogin(jwt) {
        this.syncTokenProperties(jwt);
        
        if (this.userRepository == null) {
            this.userRepository = this.$injector.get('userRepository');
        }

        this.userRepository.getUserProfile().then((userProfile: IUserProfile) => {

            this.setUserProfile(userProfile);

            let redirectState = localStorage.getItem(jwt.state);

            console.log('Redirecting to: ' + redirectState);
            localStorage.removeItem(jwt.state);

            if (!angular.isDefined(redirectState) || redirectState == null) {
                redirectState = this.wizardStateName;
            }

            this.screenLockService.broadcast(0);

            this.navigate(redirectState);
        });
    }

    public refreshToken() {
        let svc = this;
        var silentAuthDependency = this.$q.defer();
        let hasPendingRequests = this.pendingCalls.length > 0;
        if (!hasPendingRequests) {
            this.pendingCalls.push(silentAuthDependency);

            this.authenticationGateway.refreshToken().then(function (jwt) {
                svc.syncTokenProperties(jwt);
                svc.pendingCalls.forEach(function (d) { d.resolve(jwt) });
            }, function (err) {
                svc.pendingCalls.forEach(function (d) { d.reject(err) });
                svc.logout();
            }
            ).finally(function () {
                svc.pendingCalls = [];
            });
        } else {
            this.pendingCalls.push(silentAuthDependency);
        }
        return silentAuthDependency.promise;
    }

    public logout() {
        localStorage.removeItem(SessionManagement.IsAuthenticated);
        localStorage.removeItem(SessionManagement.UserProfile);
        localStorage.removeItem(SessionManagement.Token);

        this.accessToken = '';
        this.idToken = '';

        this.authenticationGateway.logout();

        this.navigate(this.logoutRedirectStateName);
    }

    public isAuthenticated() {
        return localStorage.getItem(SessionManagement.IsAuthenticated) === 'true';
    }

    public isInRole(roles: string[]) {

        let userProfile = this.getUserProfile();
        let assignedRoles = userProfile ? userProfile.roles : [];

        let userIsInRole = assignedRoles.length > 0;

        if (!userIsInRole)
            return userIsInRole;

        if (roles && roles.length > 0) {
            userIsInRole = assignedRoles.some(r => roles.indexOf(r) >= 0);
        }
        return userIsInRole;
    }

    private navigate(stateName: string) {

        if (this.$state == null) {
            this.$state = this.$injector.get('$state');
        }

        if (this.$state) {
            this.$state.go(stateName);
        }
    }

    private syncTokenProperties(jwt){
        this.idToken = jwt.idToken;
        this.setAccessToken(jwt.accessToken);
        const tokenJson = JSON.stringify(jwt);
        localStorage.setItem(SessionManagement.Token, tokenJson);
    }

    private getGuid(): string {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
}